// Nova API Client for real file uploads and API calls

export interface SynkCryptResponse {
  success: boolean;
  encryptedFile?: File;
  integrationUrl?: string;
  error?: string;
}

export interface SynkSmashResponse {
  success: boolean;
  integrationUrl?: string; // Changed from shareUrl to integrationUrl for consistency
  fileId?: string;
  error?: string;
}

export interface SynkPassResponse {
  success: boolean;
  password?: string;
  targetId?: string;
  error?: string;
}

export interface SynkSwapResponse {
  success: boolean;
  transactionHash?: string;
  amount?: string;
  error?: string;
}

class NovaApiClient {
  private baseUrls = {
    synkCrypt: "https://synkcrypt.synk.ws",
    synkSmash: "https://smash.synk.ws",
    synkPass: "https://synkpass.synk.ws",
    synkSwap: "https://swap.synk.ws",
  };

  // Upload file to SynkCrypt for encryption
  async uploadToSynkCrypt(file: File): Promise<SynkCryptResponse> {
    try {
      console.log(
        "🔐 Nova - Uploading file to SynkCrypt:",
        file.name,
        file.size
      );

      const formData = new FormData();
      formData.append("file", file);
      formData.append("action", "encrypt");

      const response = await fetch(`${this.baseUrls.synkCrypt}/api/upload/`, {
        method: "POST",
        body: formData,
        headers: {
          // Don't set Content-Type, let browser set it with boundary for FormData
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ SynkCrypt upload successful:", result);

      return {
        success: true,
        integrationUrl:
          result.integrationUrl || `${this.baseUrls.synkCrypt}/encrypt/`,
        encryptedFile: result.encryptedFile,
      };
    } catch (error) {
      console.error("❌ SynkCrypt upload failed:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Upload encrypted file to SynkSmash for P2P sharing
  async uploadToSynkSmash(file: File): Promise<SynkSmashResponse> {
    try {
      console.log(
        "📤 Nova - Uploading encrypted file to SynkSmash:",
        file.name,
        file.size
      );
      console.log(
        "📤 Nova - SynkSmash API URL:",
        `${this.baseUrls.synkSmash}/api/upload/`
      );

      const formData = new FormData();
      formData.append("file", file);
      formData.append("action", "share");
      formData.append("secure", "true");

      console.log("📤 Nova - FormData prepared:", {
        file: file.name,
        action: "share",
        secure: "true",
      });

      const response = await fetch(`${this.baseUrls.synkSmash}/api/upload`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ SynkSmash upload successful:", result);

      return {
        success: true,
        integrationUrl:
          result.integrationUrl || // Use integrationUrl from API response
          `${this.baseUrls.synkSmash}/share/${result.fileId}`,
        fileId: result.fileId,
      };
    } catch (error) {
      console.error("❌ SynkSmash upload failed:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Generate password with SynkPass
  async generatePasswordWithSynkPass(
    targetId: string,
    username: string
  ): Promise<SynkPassResponse> {
    try {
      console.log("🔑 Nova - Generating password with SynkPass:", {
        targetId,
        username,
      });

      const response = await fetch(`${this.baseUrls.synkPass}/api/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          targetId,
          username,
          strength: "strong",
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ SynkPass password generation successful:", result);

      return {
        success: true,
        password: result.password,
        targetId: result.targetId,
      };
    } catch (error) {
      console.error("❌ SynkPass password generation failed:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Execute swap with SynkSwap
  async executeSwapWithSynkSwap(
    fromAmount: string,
    fromToken: string,
    toToken: string
  ): Promise<SynkSwapResponse> {
    try {
      console.log("💱 Nova - Executing swap with SynkSwap:", {
        fromAmount,
        fromToken,
        toToken,
      });

      const response = await fetch(`${this.baseUrls.synkSwap}/api/swap`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fromAmount,
          fromToken,
          toToken,
          slippage: "0.5",
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ SynkSwap execution successful:", result);

      return {
        success: true,
        transactionHash: result.transactionHash,
        amount: result.amount,
      };
    } catch (error) {
      console.error("❌ SynkSwap execution failed:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Fallback method if APIs are not available - returns mock data
  async mockApiCall(service: string, action: string, data: any): Promise<any> {
    console.log(`🔄 Nova - Using mock API for ${service} ${action}:`, data);

    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));

    switch (service) {
      case "synkcrypt":
        return {
          success: true,
          integrationUrl: `${this.baseUrls.synkCrypt}/encrypt?file=${
            data.file?.name || "uploaded"
          }`,
          encryptedFile: data.file,
        };

      case "synksmash":
        return {
          success: true,
          integrationUrl: `${this.baseUrls.synkSmash}/share/mock-file-id-123`,
          fileId: "mock-file-id-123",
        };

      case "synkpass":
        return {
          success: true,
          password: "GeneratedPassword123!",
          targetId: data.targetId,
        };

      case "synkswap":
        return {
          success: true,
          transactionHash: "0x1234567890abcdef...",
          amount: data.fromAmount,
        };

      default:
        return { success: false, error: "Unknown service" };
    }
  }
}

export const novaApiClient = new NovaApiClient();
export default novaApiClient;
